import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import Section from '../../../components/Main/Section/Section';
import GoogleMyBusinessLogin from '../../../components/GoogleMyBusiness/GoogleMyBusinessLogin';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import LocationCard from '../../../components/LocationCard/LocationCard';
import styled from 'styled-components';
import { GMBAuthContext } from '../../../providers/GMBAuthProvider';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import GMBService from '../../../services/GMBService';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { Add } from '@mui/icons-material';
import Pagination from '../../../components/Pagination/Pagination';
import { IGMBLocationsResponse } from '../../../shared/interfaces/IGMBLocationsResponse';
import SortContainer from '../../../components/Containers/SortContainer';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../../../providers/AuthProvider';
import { isCooldownActive, formatAvailableAt } from '../../../shared/CooldownUtils';

const GMB_REFRESH_ROLE = 'can-refresh-gmb';
const GMB_REFRESH_LIMIT = 'limitgmbrefresh';

const LocationContainer = styled.div`
  display: grid;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing(2)};
  grid-template-columns: repeat(4, 1fr);
`;

// The backend appends gmb_locations.id ASC as a tie-breaker to every clause list, so a status sort
// needs an explicit secondary "title asc" or the two groups come back in raw id order.
const ORDER_BY_CLAUSES: Record<string, string[]> = {
  'title asc': ['title asc'],
  'isActive desc': ['isActive desc', 'title asc'],
  'isActive asc': ['isActive asc', 'title asc'],
};

const Locations: FC = () => {
  const theme = useTheme();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { user, login } = useContext(GMBAuthContext);
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const { t } = useTranslation();

  const [multiActionChecked, setMultiActionChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [locationResponse, setLocationResponse] = useState<IGMBLocationsResponse>({
    locations: [],
    paging: {
      totalItems: 0,
      pagesTotal: 0,
    },
    title: [],
  });
  // TODO since our backend can now do chained sorting we can and probably should use multi-select component for orderBy
  const [orderBy, setOrderBy] = useState<string>('title asc');
  const [offset, setOffset] = useState<number>(0);
  const [pageSize, setPageSize] = useState<string>('10');
  const [page, setPage] = useState<number>(1);
  const [used, setUsed] = useState<number>(0);
  const [availableAt, setAvailableAt] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const handleMultiActionCheckboxChange = () => {
    setMultiActionChecked((prevState) => !prevState);
  };

  const handlePageNumberChange = (event: ChangeEvent<unknown>, pageNumber: number) => {
    setPage(pageNumber);
    setOffset(parseInt(pageSize) * pageNumber - parseInt(pageSize));
  };

  const handlePageSizeChange = (event: SelectChangeEvent) => {
    setOffset(0);
    setPage(1);
    setPageSize(event.target.value);
  };

  const handleClearTerm = () => {
    setSearchTerm('');
    setOffset(0);
    setPageSize('10');
  };

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();

    const fetchData = async () => {
      let data = await GMBService.getAllLocations(
        offset,
        parseInt(pageSize),
        setLoading,
        controller.signal,
        undefined,
        ORDER_BY_CLAUSES[orderBy],
        searchTerm
      );

      if (data && !('success' in data)) {
        setLocationResponse(data);
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [user, searchTerm, offset, orderBy, pageSize]);

  const loadRefreshStatus = async () => {
    try {
      const status = await GMBService.getRefreshStatus();
      if (status) {
        setUsed(status.used ?? 0);
        setAvailableAt((currentAvailableAt) => {
          const nextAvailableAt = status.availableAt ?? null;

          if (isCooldownActive(currentAvailableAt) && !isCooldownActive(nextAvailableAt)) {
            return currentAvailableAt;
          }

          return nextAvailableAt;
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!user) {
      setUsed(0);
      setAvailableAt(null);
      return;
    }

    loadRefreshStatus();
  }, [user]);

  useEffect(() => {
    if (!availableAt || !isCooldownActive(availableAt)) {
      return;
    }

    const availableAtDate = new Date(availableAt);
    const timeoutMs = availableAtDate.getTime() - Date.now();

    if (Number.isNaN(timeoutMs) || timeoutMs <= 0) {
      setAvailableAt(null);
      return;
    }

    const timeout = window.setTimeout(() => {
      setAvailableAt(null);
      void loadRefreshStatus();
    }, timeoutMs + 1000);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [availableAt]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await GMBService.refreshGmbData();
      setAvailableAt(response?.availableAt ?? null);
      await loadRefreshStatus();
    } catch (error: any) {
      setAvailableAt(error?.response?.data?.data?.availableAt ?? null);
      await loadRefreshStatus();
    } finally {
      setRefreshing(false);
    }
  };

  const cooldownActive = isCooldownActive(availableAt);
  const refreshDisabled = refreshing || cooldownActive || exceedsLimit(GMB_REFRESH_LIMIT, used + 1);
  const showRefreshButton = !!user && hasRole(GMB_REFRESH_ROLE);
  const refreshAvailableAtLabel =
    availableAt !== null && cooldownActive
      ? `${t('refresh_available_at')} ${formatAvailableAt(availableAt)}`
      : null;

  useEffect(() => {
    setCurrentView(t('locations'));
    document.title = `${t('app_branding')} ${t('locations')}`;
  }, [setCurrentView, t]);

  return (
    <Section>
      <>
        {!user ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <GoogleMyBusinessLogin login={login} />
          </Box>
        ) : (
          <ContentContainer>
            <LeftContainer>
              <ActionBar>
                {showRefreshButton && (
                  <Box sx={{ mr: theme.spacing(2) }}>
                    <Button
                      variant="contained"
                      onClick={handleRefresh}
                      disabled={refreshDisabled}
                    >
                      {t('refresh_gmb_data')}
                    </Button>
                    {refreshDisabled && refreshAvailableAtLabel && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: theme.spacing(1) }}
                      >
                        {refreshAvailableAtLabel}
                      </Typography>
                    )}
                  </Box>
                )}
                <Checkbox
                  disabled
                  checked={multiActionChecked}
                  onChange={handleMultiActionCheckboxChange}
                  sx={{ display: 'none', mr: theme.spacing(1) }}
                />
                <Box sx={{ display: 'none', ml: 'auto' }}>
                  <Button
                    disabled
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {}}
                  >
                    {t('add_location')}
                  </Button>
                </Box>
              </ActionBar>
              <SortContainer>
                {locationResponse.paging && (
                  <Typography
                    variant="subtitle2"
                    sx={{ my: theme.spacing(2) }}
                  >
                    {locationResponse.paging.totalItems}{' '}
                    {locationResponse.paging.totalItems > 1 ? t('locations') : t('location')}
                  </Typography>
                )}
                <FormControl
                  size="small"
                  sx={{ minWidth: '284px' }}
                >
                  <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                  <Select
                    labelId="sort-select-label"
                    value={orderBy}
                    onChange={(event: SelectChangeEvent) => {
                      setOffset(0);
                      setPage(1);
                      setOrderBy(event.target.value);
                    }}
                    label={t('sort')}
                  >
                    <MenuItem value="title asc">{t('alphabetical_asc')}</MenuItem>
                    <MenuItem value="isActive desc">{t('active_first')}</MenuItem>
                    <MenuItem value="isActive asc">{t('inactive_first')}</MenuItem>
                  </Select>
                </FormControl>
              </SortContainer>
              <TextField
                id="outlined-basic"
                label={t('search_locations')}
                variant="outlined"
                sx={{ mb: theme.spacing(2) }}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchTerm && (
                        <IconButton
                          aria-label="clear"
                          onClick={handleClearTerm}
                        >
                          <CloseIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {loading ? (
                <Box>
                  <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
                </Box>
              ) : (
                <LocationContainer>
                  {locationResponse.locations.map((location) => {
                    return (
                      <LocationCard
                        key={location.ID}
                        ID={location.ID}
                        locationId={location.locationID}
                        companyName={location.locationData.title}
                        addressLines={location.locationData.address.addressLines}
                        image={undefined}
                        zip={location.locationData.address.postalCode}
                        city={location.locationData.address.locality}
                        times={location.locationData.times}
                        status={location.locationData.status}
                        verificationStatus={'VERIFIED'}
                        phoneNumber={location.locationData.phoneNumber}
                        isActive={location.isActive}
                        allowReviewResponses={location.allowReviewResponses}
                        communityManagement={location.communityManagement}
                      />
                    );
                  })}
                </LocationContainer>
              )}
              {locationResponse.paging && locationResponse.paging.pagesTotal > 1 && (
                <Pagination
                  count={locationResponse.paging.pagesTotal}
                  page={page}
                  itemDescription={t('locations')}
                  showItemsPerPage={pageSize}
                  onPageChange={handlePageNumberChange}
                  onItemsPerPageChange={handlePageSizeChange}
                />
              )}
            </LeftContainer>
          </ContentContainer>
        )}
      </>
    </Section>
  );
};

export default Locations;
